import { Permissions } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSnackbar } from 'notistack';

const SNACKBAR_ERROR_KEY = 'error-snackbar';

type HandleErrorParams = {
	errorCode: string;
	friendlyMessage?: string;
	error: Error | unknown;
};

export const useErrorHandler = () => {
	const { hasPermission } = usePermissions();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const fireErrorEvent = useAnalyticsEvent('Error');

	/**
	 * @description Handle errors by showing a friendly message to the user and
	 * if the user has Devtools.Read permissions it will display an
	 * additional snackbar with the error details. It will also fire an Error event to the analytics server
	 *
	 * @param {HandleErrorParams} options - Options object.
	 * @param {string} options.errorCode - 'PascalCaseError' <-- Should end with '*Error'
	 * @param {string} options.friendlyMessage - The error that shoild be shown to the user in a snackbar, optional?
	 * @param {Error | unknown} options.error - The error instance given by the try-catch etc.
	 */
	const handleError = ({
		errorCode,
		friendlyMessage,
		error,
	}: HandleErrorParams) => {
		fireErrorEvent({
			name: errorCode,
			details: `${error}`,
		});
		if (friendlyMessage) {
			enqueueSnackbar(friendlyMessage, {
				variant: 'warning',
			});
		}
		console.error(
			`Please report the following code to an Asaph support staff if needed:`,
		);
		console.error(`[${errorCode}]`, error);
		if (hasPermission(Permissions.DevtoolsRead)) {
			enqueueSnackbar(`[${errorCode}] ${error}`, {
				key: SNACKBAR_ERROR_KEY,
				variant: 'warning',
				persist: true,
				onClick: () => {
					closeSnackbar(SNACKBAR_ERROR_KEY);
				},
			});
		}
	};

	return { handleError };
};
